import { useState, useEffect, useCallback } from 'react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';

export interface WebAuthnCredential {
  id: string;
  user_id: string;
  credential_id: string;
  credential_name: string | null;
  device_type: string | null;
  authenticator_type: string | null;
  rp_id: string | null;
  device_id: string | null;
  created_at: string;
  last_used_at: string | null;
}

export interface SecuritySettings {
  biometric_enabled: boolean;
  biometric_unlock_enabled: boolean;
  biometric_login_enabled: boolean;
  passkey_enabled: boolean;
  lock_on_resume_enabled: boolean;
  lock_grace_period_seconds: number;
}

// Check if WebAuthn is supported
export const isWebAuthnSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
  );
};

// Check if platform authenticator (Face ID/Touch ID) is available
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Check if conditional mediation (auto-trigger passkey) is available
export const isConditionalMediationAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;
  try {
    // @ts-ignore - TypeScript may not have this type yet
    return await PublicKeyCredential.isConditionalMediationAvailable?.() ?? false;
  } catch {
    return false;
  }
};

// invoke() reports non-2xx answers as a generic error; the server's message is in the response body
const functionErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
  try {
    const body = await (error as { context?: Response })?.context?.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Runs a passkey sign-in ('verify', returns session tokens or a magic-link url)
 * or an app unlock ('unlock', needs the user's session) against webauthn-authenticate.
 */
export const authenticatePasskey = async (
  action: 'verify' | 'unlock',
  allowCredentialIds?: string[],
): Promise<{ success: true; access_token?: string; refresh_token?: string; url?: string }> => {
  const { data, error } = await supabase.functions.invoke('webauthn-authenticate', {
    body: { action: 'get-challenge' },
  });
  if (error || !data?.options) {
    throw new Error(await functionErrorMessage(error, 'Failed to get authentication challenge'));
  }

  const credential = await startAuthentication({
    optionsJSON: {
      ...data.options,
      allowCredentials: allowCredentialIds?.map((id) => ({ id, type: 'public-key' as const })),
    },
  });

  const { data: result, error: verifyError } = await supabase.functions.invoke('webauthn-authenticate', {
    body: { action, challengeId: data.challengeId, credential, redirectUrl: window.location.origin },
  });
  if (verifyError || !result?.success) {
    throw new Error(await functionErrorMessage(verifyError, 'Authentication failed'));
  }
  return result;
};

export const useBiometricAuth = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(false);
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false);

  // Check browser support on mount
  useEffect(() => {
    const checkSupport = async () => {
      setIsSupported(isWebAuthnSupported());
      setHasPlatformAuth(await isPlatformAuthenticatorAvailable());
    };
    checkSupport();
  }, []);

  // Fetch credentials and settings
  const fetchData = useCallback(async () => {
    if (!user) {
      setCredentials([]);
      setSettings(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch credentials
      const { data: credData, error: credError } = await supabase
        .from('webauthn_credentials')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (credError) throw credError;
      setCredentials(credData || []);

      // Fetch or create security settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_security_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      if (settingsData) {
        setSettings({
          biometric_enabled: settingsData.biometric_enabled,
          biometric_unlock_enabled: settingsData.biometric_unlock_enabled,
          biometric_login_enabled: settingsData.biometric_login_enabled,
          passkey_enabled: settingsData.passkey_enabled,
          lock_on_resume_enabled: settingsData.lock_on_resume_enabled ?? false,
          lock_grace_period_seconds: settingsData.lock_grace_period_seconds ?? 60,
        });
      } else {
        // Default settings
        setSettings({
          biometric_enabled: false,
          biometric_unlock_enabled: false,
          biometric_login_enabled: false,
          passkey_enabled: false,
          lock_on_resume_enabled: false,
          lock_grace_period_seconds: 60,
        });
      }
    } catch (error) {
      console.error('Error fetching biometric data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Register a new passkey
  const registerPasskey = useCallback(async (credentialName?: string): Promise<boolean> => {
    if (!user || !isSupported) {
      toast.error('Passkeys are not supported on this device');
      return false;
    }

    try {
      // Get registration challenge from server
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('webauthn-register', {
        body: { action: 'get-challenge' }
      });

      if (challengeError || !challengeData?.options) {
        throw new Error(await functionErrorMessage(challengeError, 'Failed to get registration challenge'));
      }

      // Start WebAuthn registration
      const credential = await startRegistration({
        optionsJSON: challengeData.options
      });

      // Determine authenticator type from response
      let authenticatorType = 'Passkey';
      if (credential.authenticatorAttachment === 'platform') {
        // Try to determine if it's Face ID or Touch ID based on platform
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('mac') || ua.includes('iphone') || ua.includes('ipad')) {
          authenticatorType = ua.includes('iphone') || ua.includes('ipad') ? 'Face ID' : 'Touch ID';
        } else if (ua.includes('windows')) {
          authenticatorType = 'Windows Hello';
        } else if (ua.includes('android')) {
          authenticatorType = 'Fingerprint';
        }
      }

      // Server verifies the attestation against its challenge before storing the key
      const { error: verifyError } = await supabase.functions.invoke('webauthn-register', {
        body: {
          action: 'verify',
          challengeId: challengeData.challengeId,
          credential,
          credentialName: credentialName || authenticatorType,
          deviceType: credential.authenticatorAttachment || 'platform',
          authenticatorType,
        }
      });

      if (verifyError) {
        throw new Error(await functionErrorMessage(verifyError, 'Failed to register passkey'));
      }

      toast.success('Passkey registered successfully');
      await fetchData(); // Refresh data
      return true;
    } catch (error: any) {
      console.error('Passkey registration error:', error);

      // Handle user cancellation
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled');
      } else {
        toast.error(error.message || 'Failed to register passkey');
      }
      return false;
    }
  }, [user, isSupported, fetchData]);

  // Biometric unlock (for session continuation)
  const biometricUnlock = useCallback(async (): Promise<boolean> => {
    if (!user || !isSupported || credentials.length === 0) {
      return false;
    }

    try {
      // Offer only this user's passkeys; the server also checks the passkey belongs to the session user
      await authenticatePasskey('unlock', credentials.map(c => c.credential_id));
      return true;
    } catch (error: any) {
      console.error('Biometric unlock error:', error);
      return false;
    }
  }, [user, isSupported, credentials]);

  // Delete a passkey
  const deletePasskey = useCallback(async (credentialId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('webauthn_credentials')
        .delete()
        .eq('id', credentialId);

      if (error) throw error;

      // Update local state
      setCredentials(prev => prev.filter(c => c.id !== credentialId));

      // If no more credentials, disable passkey settings
      if (credentials.length <= 1) {
        await updateSettings({
          passkey_enabled: false,
          biometric_enabled: false,
          biometric_unlock_enabled: false,
          biometric_login_enabled: false,
        });
      }

      toast.success('Passkey removed');
      return true;
    } catch (error) {
      console.error('Error deleting passkey:', error);
      toast.error('Failed to remove passkey');
      return false;
    }
  }, [credentials.length]);

  // Update security settings
  const updateSettings = useCallback(async (newSettings: Partial<SecuritySettings>): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_security_settings')
        .upsert({
          user_id: user.id,
          ...settings,
          ...newSettings,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setSettings(prev => prev ? { ...prev, ...newSettings } : null);
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
      return false;
    }
  }, [user, settings]);

  return {
    credentials,
    settings,
    isLoading,
    isSupported,
    hasPlatformAuth,
    registerPasskey,
    biometricUnlock,
    deletePasskey,
    updateSettings,
    refreshData: fetchData,
  };
};
